import {areIntervalsOverlapping} from 'date-fns';
import {useLocalStorage} from '@vueuse/core';
import {z} from 'zod';
import {defineStore, skipHydrate} from 'pinia';
import {countBy, find, map, remove as removeItems, sum} from 'lodash-es';
import type {OfferResult, OrderItemParams, OrderRequestParams} from '~/openapi';
import {getMealOrderableInterval} from '~/utils/goos';

export interface UserData {
	name: OrderRequestParams['gbCustomerName']
	surname: OrderRequestParams['gbCustomerSurname']
	email: OrderRequestParams['gbCustomerEmail']
	phone: OrderRequestParams['gbCustomerPhoneNumber']
	customerNote?: OrderRequestParams['customerNote']
	personalData?: boolean
}

export interface PickupTimeData {
	beginDateTime: Date | null
	takeoutCounterId: number
}

export enum ProductionTypeIdEnum {
	RESTAURANT = 1,
	BAKERY = 2,
	BUTCHERY = 3,
	CATERING = 5,
}

export interface IShoppingCartState {
	hypermarket: string
	currentBusiness: ProductionTypeIdEnum | null
	itemsByBusiness: Record<ProductionTypeIdEnum, OfferResult[]>
	pickupTime: PickupTimeData
	testUser: string // TODO: nahradit spravnym formatem dle potreb api
	userData: UserData
}

export class IncompatiblePickupTimesError extends Error {
	constructor(message: string, public newItem: OfferResult, public offendingItem: OfferResult) {
		super(message);
		this.name = 'IncompatiblePickupTimesError';
	}
}

const localStorageSchema = z.object({
	hypermarket: z.string(),
	currentBusiness: z.nativeEnum(ProductionTypeIdEnum).nullable(),
	itemsByBusiness: z.object({
		[ProductionTypeIdEnum.RESTAURANT]: z.any().array(),
		[ProductionTypeIdEnum.BAKERY]: z.any().array(),
		[ProductionTypeIdEnum.BUTCHERY]: z.any().array(),
		[ProductionTypeIdEnum.CATERING]: z.any().array(),
	}),
	pickupTime: z.object({
		beginDateTime: z.coerce.date().nullable(),
		takeoutCounterId: z.number(),
	}),
	testUser: z.string(),
	userData: z.object({
		name: z.string(),
		surname: z.string(),
		email: z.string(),
		phone: z.string(),
		customerNote: z.string().optional(),
		personalData: z.boolean().optional(),
	}),
});

function getInitialState(): IShoppingCartState {
	return {
		hypermarket: '',
		currentBusiness: null,
		itemsByBusiness: {
			[ProductionTypeIdEnum.RESTAURANT]: [],
			[ProductionTypeIdEnum.BAKERY]: [],
			[ProductionTypeIdEnum.BUTCHERY]: [],
			[ProductionTypeIdEnum.CATERING]: [],
		},
		pickupTime: {
			beginDateTime: null,
			takeoutCounterId: 0,
		},
		testUser: '',
		userData: {
			name: '',
			surname: '',
			email: '',
			phone: '',
			customerNote: null,
		},
	};
}

export const useShoppingCartStore = defineStore('shoppingCart', () => {
	const state = useLocalStorage(
		'cart',
		getInitialState(),
		{
			// Do not listen to storage changes to prevent double-triggering of watchers
			listenToStorageChanges: false,
			serializer: {
				read: (value) => localStorageSchema.safeParse(JSON.parse(value)).data ?? getInitialState(),
				write: (value) => JSON.stringify(value),
			},
		},
	);

	const items = computed(() => (
		state.value.currentBusiness === null
			? []
			: state.value.itemsByBusiness[state.value.currentBusiness] ?? []
	));

	const totalPrice = computed(() => {
		return items.value.reduce((acc, item) => acc + (item.price?.price ?? 0), 0);
	});

	const itemsById = computed(() => {
		return countBy(items.value ?? [], 'offerId');
	});

	const limitValidationError = computed(() => {
		const {currentBusiness} = state.value;
		if (currentBusiness === ProductionTypeIdEnum.BUTCHERY) {
			const currWeight = sum(map(items.value, (item: OfferResult) => item.meal?.mealWeightGrams || 0));
			if (currWeight < 2000) {
				return 'Pro dokončení objednávky objednejte prosím alespoň 2 kg masa nebo uzenin.';
			}
			return null;
		}

		return null;
	});

	const currentBusiness = computed(() => state.value.currentBusiness);
	const getHypermarket = computed(() => state.value.hypermarket);
	const getPickupTime = computed(() => state.value.pickupTime);
	const getUserData = computed(() => state.value.userData);
	const getUniqueItems = computed(() => {
		if (state.value.currentBusiness === null) {
			return [];
		}

		const unique = state.value.itemsByBusiness[state.value.currentBusiness]?.filter((v, i, a) =>
			a.findIndex((t) => (t.offerId === v.offerId && t.mealNameOffer === v.mealNameOffer)) === i,
		) ?? [];
		unique.sort((a, b) => a.mealNameOffer?.localeCompare(b.mealNameOffer ?? '') ?? 0);
		return unique;
	});

	const findItem = (item: OrderItemParams): OrderItemParams | undefined => {
		if (state.value.currentBusiness === null) {
			return undefined;
		}
		return find(state.value.itemsByBusiness[state.value.currentBusiness], (cartItem) => cartItem.offerId === item.offerId);
	};

	function add(item: any) {
		if (state.value.currentBusiness === null) {
			return;
		}

		const newItemInterval = getMealOrderableInterval(item);

		if (!newItemInterval) {
			return;
		}

		const cartItems = state.value.itemsByBusiness[state.value.currentBusiness];

		const offendingProduct = cartItems.find((cartItem) => {
			const cartItemInterval = getMealOrderableInterval(cartItem);
			return !!cartItemInterval && !areIntervalsOverlapping(newItemInterval, cartItemInterval);
		});

		if (offendingProduct) {
			throw new IncompatiblePickupTimesError('Nelze přidat produkt do košíku', item, offendingProduct);
		}

		cartItems.push(item);
	}

	function remove(item: any) {
		if (state.value.currentBusiness === null) {
			return;
		}

		const foundItem = findItem(item);
		if (foundItem) {
			const index = state.value.itemsByBusiness[state.value.currentBusiness].indexOf(foundItem);
			if (index !== -1) {
				state.value.itemsByBusiness[state.value.currentBusiness].splice(index, 1);
			}
		}
	}

	function removeAll(item: any) {
		if (state.value.currentBusiness === null) {
			return;
		}

		removeItems(state.value.itemsByBusiness[state.value.currentBusiness], item);
	}

	function empty() {
		if (state.value.currentBusiness === null) {
			return;
		}

		state.value.itemsByBusiness[state.value.currentBusiness] = [];
	}

	function removeOutOfStock(offerId: number[]) {
		if (state.value.currentBusiness === null) {
			return;
		}

		const oldItems = state.value.itemsByBusiness[state.value.currentBusiness];
		const newItems = oldItems.filter((item) => (
			item.availability?.id !== 3
			&& !!item.offerId
			&& !offerId.includes(item.offerId)
		));

		// Only replace the array if the length has changed
		if (newItems.length !== oldItems.length) {
			state.value.itemsByBusiness[state.value.currentBusiness] = newItems;
		}
	}

	function addPickupTime(data: PickupTimeData) {
		state.value.pickupTime = data;
	}

	function resetPickupTime() {
		state.value.pickupTime = {
			beginDateTime: null,
			takeoutCounterId: 0,
		};
	}

	function setInOnePiece(item: OrderItemParams, inOnePiece: boolean) {
		const foundItem = findItem(item);
		if (foundItem) {
			foundItem.inOnePiece = inOnePiece;
		}
	}

	function getInOnePiece(item: OrderItemParams) {
		const foundItem = findItem(item);
		return foundItem?.inOnePiece ?? false;
	}

	function setUserData(data: UserData) {
		state.value.userData.name = data.name;
		state.value.userData.surname = data.surname;
		state.value.userData.email = data.email;
		state.value.userData.phone = data.phone;
		state.value.userData.customerNote = data.customerNote ?? null;
	}

	return {
		state: skipHydrate(state),
		items: skipHydrate(items),
		totalPrice: skipHydrate(totalPrice),
		add,
		remove,
		removeAll,
		empty,
		removeOutOfStock,
		itemsById,
		limitValidationError,
		getHypermarket,
		getPickupTime,
		addPickupTime,
		resetPickupTime,
		getUserData,
		setUserData,
		getUniqueItems,
		currentBusiness,
		setInOnePiece,
		getInOnePiece,
	};
});
